import React, { useMemo, useState } from "react"
import { useFormatProduct } from "@app/scripts/hooks/use-format-product"
import { wishlist, wishlistFilled } from "../hooks/icons"
import { ProductCardMedia } from "./product-card-media"
import {
  isMobile,
  parseIfString,
  useProductBadge,
  decodeHTMLEntities,
} from "@app/scripts/lib/utils"
import { ProductCardSwatches } from "./product-card-swatches"
import { SearchspringSearchResult, Variant, countryCurrency } from "@app/types"
import { getCurrentStoreConfig } from "@app/config"

type Props = Parameters<typeof useFormatProduct>[0] & {
  data: SearchspringSearchResult
  instantSearch?: boolean
  productCarousel?: boolean
  modelToggle?: boolean
  wishlistItemId?: string // when set, the wishlist icon will be active
  displayWishlistButton?: boolean
  isWishlistProductGrid?: boolean
  onWishlistClick?: (wishlistItemId?: string) => void
  extraBottomContent?: React.ReactNode
  swatchColours?: { name: string; hex: string }[]
  currentCountryCurrency?: countryCurrency
}

export function CardProduct({
  data,
  instantSearch = false,
  productCarousel = false,
  modelToggle,
  wishlistItemId,
  displayWishlistButton = true,
  isWishlistProductGrid = false,
  onWishlistClick,
  extraBottomContent,
  swatchColours = [],
  currentCountryCurrency,
}: Props) {
  const [hoveredSiblingHandle, setHoveredSiblingHandle] = useState<
    string | null
  >(null)

  const {
    handle,
    name,
    url,
    imageUrl,
    status,
    ss_instock_pct,
    images,
    variants,
    options,
    compareAtPrice,
    price,
    mfield_dotapparel21_siblings,
    intellisuggestData,
    intellisuggestSignature,
  } = data

  const hasCompareAtPrice = (compareAtPrice > 0 && compareAtPrice > price) || ""

  const available = status && ss_instock_pct > 1
  const productPhotos =
    (typeof images === "string" ? images.split("|") : images) ??
    (imageUrl ? [imageUrl] : [])

  const productVariantData = parseIfString(variants)
  const productSiblingData = parseIfString(mfield_dotapparel21_siblings)

  const badge = React.useMemo(
    () => useProductBadge(data, available),
    [data, available]
  )

  const currentProductColour = useMemo(
    () =>
      parseIfString(options)
        .find(
          (option: { name: string; values: string[] }) =>
            option.name.toLowerCase() === "colour"
        )
        ?.values[0]?.replace(/\s+/g, "-")
        .toLowerCase() || "",
    [options]
  )

  const siteId = getCurrentStoreConfig().searchspring.products

  return (
    <div
      className={`product-card ss-product-card--container ${instantSearch && "product-card--instant-search"}`}
    >
      <div className="product-card--featured-image product-card--photo-container">
        <div className="product-card--badge-swatches-container">
          <div
            className={`product-card--badges left top ${instantSearch && "is-hidden"} `}
          >
            {badge}
          </div>

          {!isMobile && !instantSearch && (
            <ProductCardSwatches
              mobile={false}
              swatches={productSiblingData}
              currentHandle={handle}
              currentProductColour={currentProductColour}
              hoveredSiblingHandle={hoveredSiblingHandle}
              setHoveredSiblingHandle={setHoveredSiblingHandle}
              swatchColours={swatchColours}
            />
          )}
        </div>
        {displayWishlistButton && (
          <>
            {onWishlistClick ? (
              <button
                className="product-card__wishlist-icon product-card--wishlist"
                data-product-id={data?.uid || data?.id}
                data-variant-id={
                  productVariantData[0]?.id || data?.variants?.[0]?.id
                }
                onClick={
                  onWishlistClick
                    ? () => onWishlistClick?.(wishlistItemId)
                    : undefined
                }
              >
                {wishlistItemId ? wishlistFilled : wishlist}
              </button>
            ) : (
              <>
                <button
                  className="product-card__wishlist-icon product-card--wishlist product-card--wishlist"
                  data-product-id={data?.uid || data?.id}
                  data-variant-id={
                    productVariantData[0]?.id || data?.variants?.[0]?.id
                  }
                >
                  <span className="wishlist-icon-inactive"> {wishlist}</span>
                  <span className="wishlist-icon-active">{wishlistFilled}</span>
                </button>
              </>
            )}
          </>
        )}

        {productCarousel && isMobile ? (
          <img
            key={handle}
            src={`${productPhotos[0]}&width=600`}
            srcSet={`
       ${productPhotos[0]}&width=400 400w,
       ${productPhotos[0]}&width=600 600w
     `}
            alt={`Product Image`}
            className="product-card--photo-simple"
          />
        ) : (
          <ProductCardMedia
            media={productPhotos}
            instantSearch={instantSearch}
            modelToggle={modelToggle}
            currentHandle={handle}
            productSiblingData={productSiblingData}
            hoveredSiblingHandle={hoveredSiblingHandle}
            intellisuggestData={intellisuggestData}
            intellisuggestSignature={intellisuggestSignature}
            siteId={siteId}
          />
        )}
      </div>
      <div className="product-card-info product-card--information-container">
        <div className="product-card__main-details-container flex-container product-card--initial--info-container">
          <a
            href={url}
            onMouseDown={(event) =>
              window.IntelliSuggest?.clickItem(
                event.currentTarget as HTMLElement,
                {
                  siteId: siteId,
                  intellisuggestData: intellisuggestData,
                  intellisuggestSignature: intellisuggestSignature,
                }
              )
            }
          >
            <div className="product-card--title">
              {decodeHTMLEntities(name)?.toLowerCase()}
            </div>
          </a>
          <div
            className={`price ${instantSearch ? "is-hidden" : "is-hidden md:is-visible"}  product-card--price`}
          >
            {price > 0 && `${currentCountryCurrency?.currencySymbol}${price}`}
          </div>
        </div>

        {isMobile || instantSearch ? (
          <div className="product-card-price--container flex-container">
            {price > 0 && (
              <p className="price product-card--price">
                {currentCountryCurrency?.currencySymbol}
                {price}
              </p>
            )}

            {hasCompareAtPrice && (
              <div
                className={`price ${!instantSearch ? "" : "is-hidden"}  product-card--sale-price`}
              >
                {currentCountryCurrency?.currencySymbol}
                {compareAtPrice}
              </div>
            )}
          </div>
        ) : null}

        <div
          className={`product-card__secondary-details-container product-card--secondary-info-container ${instantSearch || isWishlistProductGrid ? "is-hidden" : ""}`}
        >
          <div className="product-card__secondary-details-container-items">
            <div
              className={`product-card__sizes-simple ${instantSearch ? "is-hidden " : ""} product-card--sizes`}
            >
              {productVariantData.length > 0 &&
                data?.handle != "gift-card" &&
                productVariantData.map(
                  (size: Variant) =>
                    size.option2 && (
                      <div
                        key={size.id}
                        className={`product-card__size ${(size as Variant & { inventory_quantity?: number }).inventory_quantity === 0 && "out-of-stock"}`}
                      >
                        <span>
                          {size.option2 === "-" ? "One Size" : size.option2}
                        </span>
                      </div>
                    )
                )}
            </div>
            {hasCompareAtPrice && (
              <div
                className={`price ${instantSearch || !isMobile ? "" : "is-hidden"}  product-card--sale-price`}
              >
                {currentCountryCurrency?.currencySymbol}
                {compareAtPrice}
              </div>
            )}
          </div>
        </div>
        {isMobile || instantSearch ? (
          <ProductCardSwatches
            mobile={true}
            swatches={productSiblingData}
            currentHandle={handle}
            currentProductColour={currentProductColour}
            swatchColours={swatchColours}
          />
        ) : null}
      </div>

      {extraBottomContent}
    </div>
  )
}
